/**
 * The entries each total of a list leaves out for picking no row of it, as
 * the last pull that caught this device up counted them (`SyncUnpickedRow`,
 * owner 29 Oct 2026). One `sync_meta` row per business, beside the setup's
 * Dashboard cards: small, replaced whole, and read by the Dashboard's line
 * under a card over a list's stored balance (`list-unpicked.ts`).
 */
import type { SyncUnpickedRow } from '@alluzo/shared';
import { eq } from 'drizzle-orm';

import { getDb } from '@/lib/db';
import { syncMeta } from '@/lib/db-schema';

const unpickedKey = (tenantId: string): string => `unpicked:${tenantId}`;

/** What the last catching-up pull counted for this business; empty when it counted none. */
export const getUnpicked = (tenantId: string): SyncUnpickedRow[] => {
  const row = getDb().select({ value: syncMeta.value }).from(syncMeta).where(eq(syncMeta.key, unpickedKey(tenantId))).get();
  if (row === undefined) return [];
  try {
    const held: unknown = JSON.parse(row.value);
    return Array.isArray(held) ? (held as SyncUnpickedRow[]) : [];
  } catch {
    return [];
  }
};

/**
 * Keep the counts a catching-up pull served, all of them in one write; none
 * deletes the row. True when they differ from what was kept, so the
 * Dashboard is told.
 */
export const saveUnpicked = (tenantId: string, rows: readonly SyncUnpickedRow[]): boolean => {
  const key = unpickedKey(tenantId);
  const held = getDb().select({ value: syncMeta.value }).from(syncMeta).where(eq(syncMeta.key, key)).get();
  if (rows.length === 0) {
    if (held === undefined) return false;
    getDb().delete(syncMeta).where(eq(syncMeta.key, key)).run();
    return true;
  }
  const value = JSON.stringify(rows);
  if (held?.value === value) return false;
  getDb().insert(syncMeta).values({ key, value }).onConflictDoUpdate({ target: syncMeta.key, set: { value } }).run();
  return true;
};
